import { IAggFunc } from "@ag-grid-community/core";
import { keepPreviousData } from "@tanstack/react-query";
import { useCapitalActivityInvestorOverviewRetrieve } from "gen/endpoints/capital-activity/capital-activity";
import { GenericTableCell } from "gen/model/genericTableCell";
import { GenericTableRow } from "gen/model/genericTableRow";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingState } from "shared/components/empty/LoadingState";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { moneyCellValue } from "shared/components/table/data/DataTableUtils";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import { sumDecimalStrings } from "shared/utils/DecimalUtils";

export interface CapitalEventBreakdownTableProps {
  fundFamilyId: string;
  fundFamilyEventId: string;
  isDraftEvent?: boolean;
}

/*
  capital_call_percent = total_capital_call / commitment
  distribution_percent = (total_distribution or total_distribution_after_fee_holdback) / commitment
  distribution_percent_after_waterfall = distribution_to_participating_partners / commitment
*/
const percentColumnIdToPossibleCapitalAmountColumnIds: Record<
  string,
  Array<string>
> = {
  capital_call_percent: ["total_capital_call"],
  distribution_percent: [
    "total_distribution",
    "total_distribution_after_fee_holdback",
  ],
  distribution_percent_after_waterfall: [
    "distribution_to_participating_partners",
  ],
};

const percentOfCommitmentAggFunc: IAggFunc<
  GenericTableRow,
  GenericTableCell
> = (params) => {
  const columnId = params.colDef.colId;
  if (
    columnId &&
    Object.keys(percentColumnIdToPossibleCapitalAmountColumnIds).includes(
      columnId,
    )
  ) {
    const capitalAmountColumnIds =
      percentColumnIdToPossibleCapitalAmountColumnIds[columnId];
    const commitmentAmountColumnId = "commitment";

    // NOTE: Not using params.rowNode.aggData here because
    // there's no guarantee that the capital amount and commitment columns
    // will be computed before this column, so we need to
    // aggregate ourselves.

    // compute capital amount total by adding up the capital amount for each column Id in capitalAmountColumnIds.
    // only one of the capitalAmountColumnIds will be present in the table.
    const capitalAmountTotal = sumDecimalStrings(
      capitalAmountColumnIds.flatMap((capitalAmountColumnId) =>
        params.rowNode.allLeafChildren.map(
          (it) => moneyCellValue(it.data?.cells[capitalAmountColumnId]) || "0",
        ),
      ),
    ).absoluteValue();
    const commitmentAmountTotal = sumDecimalStrings(
      params.rowNode.allLeafChildren.map(
        (it) => moneyCellValue(it.data?.cells[commitmentAmountColumnId]) || "0",
      ),
    );

    return {
      value: {
        type: "percent",
        value: capitalAmountTotal
          .div(commitmentAmountTotal)
          .mul(100)
          .toString(),
      },
    };
  }
};

export const CapitalEventBreakdownTable: React.FC<
  CapitalEventBreakdownTableProps
> = ({ fundFamilyId, fundFamilyEventId, isDraftEvent }) => {
  const { t } = useTranslation(["capitalActivity", "common"]);

  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);

  const { data, isFetching } = useCapitalActivityInvestorOverviewRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      is_equalized_view: isEqualizedView || undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  return (
    <TableBox isFullWidth={true}>
      <TableBoxHeader>
        <TableBoxTitle>
          {isDraftEvent
            ? t("capitalActivity:netEventSummary")
            : t("capitalActivity:netEventInvestorBreakdown")}
        </TableBoxTitle>
        <DataTableBoxActions>
          <EqualizedViewToggleButton
            isEqualizedView={isEqualizedView}
            setIsEqualizedView={setIsEqualizedView}
          />
        </DataTableBoxActions>
      </TableBoxHeader>
      <TableBoxBody>
        {data ? (
          <DataTable
            data={data}
            isLoading={isFetching}
            aggFunc={percentOfCommitmentAggFunc}
          />
        ) : (
          <LoadingState />
        )}
      </TableBoxBody>
    </TableBox>
  );
};
